package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.ScreenshotDesign

/** Undo owns view context so an edit in another language cannot appear on the wrong shot. */
internal data class HistoryView(val slotId: String, val locale: String, val device: String, val selection: Set<String> = emptySet())

/** Only identical selection, axis and view may share a keyboard-nudge entry. */
internal data class NudgeKey(val selection: Set<String>, val axis: String)

/** A bounded, session-local undo log; captions and view-only actions never enter this channel. */
internal class DesignHistory(initial: ScreenshotDesign, initialView: HistoryView, private val capacity: Int = 2_000) {
    private data class Entry(
        val before: ScreenshotDesign, val after: ScreenshotDesign, val label: String,
        val view: HistoryView, val nudge: NudgeKey?, val time: Long,
    )
    private val entries = mutableListOf<Entry>()
    private var index = 0
    var document = initial
        private set
    var view = initialView
        private set
    val canUndo: Boolean get() = index > 0
    val canRedo: Boolean get() = index < entries.size
    val size: Int get() = entries.size
    init { require(capacity in 1..2_000) }

    fun switchView(next: HistoryView) { view = next; breakCoalescing = true }
    private var breakCoalescing = false

    fun commit(next: ScreenshotDesign, label: String, now: Long, nudge: NudgeKey? = null): Boolean {
        if (next == document) return false
        val hadRedo = canRedo
        while (entries.size > index) entries.removeAt(entries.lastIndex)
        val last = entries.lastOrNull()
        if (!hadRedo && !breakCoalescing && nudge != null && last?.nudge == nudge && last.view == view &&
            now >= last.time && now - last.time < 1_000
        ) entries[entries.lastIndex] = last.copy(after = next, time = now)
        else entries += Entry(document, next, label, view, nudge, now)
        while (entries.size > capacity) entries.removeAt(0)
        index = entries.size
        document = next
        breakCoalescing = false
        return true
    }

    fun undo(restoreView: (HistoryView) -> Unit = {}): String? {
        if (!canUndo) return null
        val entry = entries[--index]
        view = entry.view
        restoreView(view)
        document = entry.before
        breakCoalescing = true
        return entry.label
    }

    fun redo(restoreView: (HistoryView) -> Unit = {}): String? {
        if (!canRedo) return null
        val entry = entries[index++]
        view = entry.view
        restoreView(view)
        document = entry.after
        breakCoalescing = true
        return entry.label
    }
}
